import { EuroIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTripPrice } from '../../../api/trips/create-trip-price'
import { updateTripPrice } from '../../../api/trips/update-trip-price'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TripPrice } from '../../../lib/types/models/trip-price/trip-price'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import PeriodBadge from '../../elements/period-badge'
import TripPriceForm from './trip-price-form'

type Props = {
  tripId: number
}

const TripPriceDataTable: React.FC<Props> = ({ tripId }) => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TripPrice
  >(['create', 'edit'])
  const tableRef = React.useRef<DynamicTableRef<TripPrice>>(null)

  const { mutateAsync: createTripPriceMutation } = useMutation({
    mutationFn: createTripPrice,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Prix ajouté avec succès')
    }
  })

  const { mutateAsync: updateTripPriceMutation } = useMutation({
    mutationFn: updateTripPrice,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Prix modifié avec succès')
    }
  })

  return (
    <div>
      <NestedDataTable<TripPrice, null>
        ref={tableRef}
        title={'Liste des prix'}
        deleteMessage={{
          name: 'prix',
          attributeName: 'price',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer le prix ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces prix ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les prix ?'
        }}
        columns={[
          {
            accessorKey: 'periods',
            header: 'Périodes',
            cell: ({ row }) => {
              const periods = row.original.periods
              return periods.length === 0 ? (
                <span>-</span>
              ) : (
                <div className={'flex flex-wrap w-96 py-3 gap-1'}>
                  {periods.map((period, index) => (
                    <PeriodBadge key={index} period={period} />
                  ))}
                </div>
              )
            }
          },
          {
            accessorKey: 'price',
            header: 'Prix',
            enableSorting: true,
            cell: ({ row }) => `${row.original.price} €`
          }
        ]}
        onEditAction={(tripPrice) => toggleModal('edit', tripPrice)}
        endpoint={API.trips.getTripPrices.url.replace(':id', tripId.toString())}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter un prix',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un prix'}
        icon={EuroIcon}
        hideActions
        onCancel={() => closeModel('create')}
      >
        <TripPriceForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => {
              await createTripPriceMutation({ tripId, ...values })
              closeModel('create')
            }
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier un prix'}
          icon={EuroIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <TripPriceForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              ...selectedData,
              periods: selectedData.periods.map((period) => period.id)
            }}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: async (values) =>
                updateTripPriceMutation({ id: selectedData.id, tripId, ...values })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripPriceDataTable
